import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import logoBleu from "@/assets/logo-bleu.png.asset.json";
import { breadcrumbJsonLd } from "@/content/schema";
import { ORG_ID, OG_IMAGE, PERSON_ID, SITE, abs } from "@/content/siteConfig";
import { BLOG_TITLE_OVERRIDES, BLOG_META_DESCRIPTION_OVERRIDES, BLOG_HEAD_DESCRIPTION_OVERRIDES, BLOG_PREVIOUS_SEO_TITLES } from "@/content/blogTitleOverrides";
import { BLOG_SHARE_IMAGES } from "@/content/blogShareImages";
import { BLOG_FAQS } from "@/content/blogFaqs";
import { faqJsonLd } from "@/content/faqs";
import { pushAuditStart } from "@/lib/analytics";
import { BlogCard } from "@/components/marketing/BlogCard";
import { keepReadingPosts } from "@/lib/blogNavigation";
import { enhanceArticle } from "@/lib/articleEnhance";
import { OnThisPage } from "@/components/marketing/OnThisPage";
import { addBlogIntroSection } from "@/content/blogSectionAdditions";


interface PostFull {
  id: string;
  slug: string;
  title: string;
  description: string;
  content: string;
  category: string;
  keywords: string[];
  reading_minutes: number;
  published_at: string;
  modified_at: string | null;
}

export const Route = createFileRoute("/blog/$slug")({
  loader: async ({ params }) => {
    const { getPublishedPost, listSameCategoryPosts } = await import("@/lib/blogPosts");
    const data = await getPublishedPost(params.slug);
    if (!data) throw notFound();
    const candidates = await listSameCategoryPosts(data.category, data.slug);
    const enhanced = enhanceArticle(data.content);
    const withAddition = addBlogIntroSection(data.slug, enhanced.html);
    const additionToc = withAddition === enhanced.html ? enhanced.toc : enhanceArticle(withAddition).toc;
    return { post: { ...(data as PostFull), content: withAddition }, toc: additionToc, relatedPosts: keepReadingPosts(candidates ?? [], data) };
  },
  head: ({ loaderData }) => {
    const post = loaderData?.post;
    if (!post) return {};
    const url = abs(`/blog/${post.slug}`);
    const seoTitle = BLOG_TITLE_OVERRIDES[post.slug] ?? post.title;
    const seoDescription = BLOG_META_DESCRIPTION_OVERRIDES[post.slug] ?? post.description;
    const metaDescription = BLOG_HEAD_DESCRIPTION_OVERRIDES[post.slug] ?? seoDescription;
    const share = BLOG_SHARE_IMAGES[post.slug];
    const shareUrl = share ? abs(share.url) : OG_IMAGE;
    return {
      meta: [
        { title: seoTitle },
        { name: "description", content: metaDescription },
        { name: "keywords", content: post.keywords.join(", ") },
        { property: "og:title", content: seoTitle },
        { property: "og:description", content: metaDescription },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { property: "og:image", content: shareUrl },
        ...(share
          ? [
              { property: "og:image:width", content: String(share.width ?? 1600) },
              { property: "og:image:height", content: String(share.height ?? 900) },
              { property: "og:image:alt", content: share.alt },
            ]
          : []),
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: shareUrl },
        { name: "twitter:title", content: BLOG_PREVIOUS_SEO_TITLES[post.slug] ?? seoTitle },
        { name: "twitter:description", content: metaDescription },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Blog", path: "/blog" },
            { name: post.title, path: `/blog/${post.slug}` },
          ]),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: seoTitle,
            description: seoDescription,
            image: share
              ? {
                  "@type": "ImageObject",
                  url: shareUrl,
                  width: share.width ?? 1600,
                  height: share.height ?? 900,
                }
              : [OG_IMAGE],
            datePublished: post.published_at,
            dateModified: post.modified_at ?? post.published_at,
            author: { "@id": PERSON_ID },
            publisher: {
              "@type": "Organization",
              "@id": ORG_ID,
              name: "Berk Hospitality Group",
              url: SITE.origin,
              logo: {
                "@type": "ImageObject",
                url: abs(`${logoBleu.url}`),
              },
            },
            articleSection: post.category,
            keywords: post.keywords.join(", "),
            mainEntityOfPage: { "@type": "WebPage", "@id": url },
            url,
          }),
        },
        ...(BLOG_FAQS[post.slug]
          ? [
              {
                type: "application/ld+json",
                children: faqJsonLd(BLOG_FAQS[post.slug]!),
              },
            ]
          : []),
      ],
    };
  },
  component: BlogPost,
});

const BLOG_UPDATED_LABELS: Record<string, string> = {
  "google-guaranteed-badge-google-verified": "Updated October 2026",
  "local-services-ads-for-contractors": "Updated October 2026",
  "mastering-lsa-disputes-recover-20-of-monthly-ad-spend": "Updated October 2026",
  "how-to-stop-pmax-from-cannibalizing-google-ads-search-terms": "Updated October 26, 2026",
};

function BlogPost() {
  const { post, toc, relatedPosts } = Route.useLoaderData();
  return (
    <SiteShell>
    <div className="wrap blog-post-wrap" style={{ paddingBottom: 120 }}>
      <div className="blog-reading-column">
        <Link
          to="/blog"
          className="mono"
          style={{
            fontSize: 11,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: "rgba(0,0,0,0.55)",
          }}
        >
          ← Blog
        </Link>
        <div
          className="mono mt-8"
          style={{
            fontSize: 11,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: "rgba(0,47,167,0.9)",
          }}
        >
          {post.category} · By{" "}
          <Link to="/about" style={{ color: "inherit", textDecoration: "underline" }}>
            Jett Berk
          </Link>{" "}
          ·{" "}
          {new Date(post.published_at).toLocaleDateString("en-US", { timeZone: "America/Phoenix",
            month: "long",
            day: "numeric",
            year: "numeric",
          })}{" "}
          · {post.reading_minutes} min read
        </div>
        <h1
          className="mt-4"
          style={{ fontSize: "clamp(32px, 5vw, 54px)", lineHeight: 1.06, letterSpacing: "-0.02em" }}
        >
          {post.title}
        </h1>
        {BLOG_UPDATED_LABELS[post.slug] && (
          <p className="mono mt-4 text-xs text-bleu-faint">{BLOG_UPDATED_LABELS[post.slug]}</p>
        )}
        <p
          className="prose-lead mt-6"
          style={{ fontSize: 19, lineHeight: 1.55, color: "rgba(0,0,0,0.72)" }}
        >
          {post.description}
        </p>

        <OnThisPage items={toc} />

        <article
          className="blog-content mt-12"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        <section className="mt-16" aria-labelledby="keep-reading-heading">
          <h2 id="keep-reading-heading" className="text-[26px] leading-tight">Keep reading</h2>
          <div className="mt-6 grid gap-6">
            {relatedPosts.map((related) => <BlogCard key={related.slug} post={related} compact />)}
          </div>
        </section>

        <div
          className="mt-16 rounded-lg border p-6"
          style={{ borderColor: "rgba(0,0,0,0.12)", background: "#fbfaf6" }}
        >
          <div
            className="mono"
            style={{
              fontSize: 11,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: "rgba(0,0,0,0.55)",
            }}
          >
            Work with us
          </div>
          <div className="mt-2" style={{ fontSize: 20, lineHeight: 1.3 }}>
            Want a paid media audit built around your account, not a template?
          </div>
          <Link
            to="/audit"
            onClick={() => pushAuditStart("inline")}
            className="mono mt-5 inline-block rounded-full px-6 py-3 transition-opacity hover:opacity-90"
            style={{
              background: "#002FA7",
              color: "#fbfaf6",
              fontSize: 12,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
            }}
          >
            Get your free audit →
          </Link>

        </div>
      </div>
    </div>
    </SiteShell>
  );
}
